/**
 * FootballGear — Procedural football helmet, facemask, and shoulder pads.
 *
 * Built from primitive geometry so we don't depend on external assets for the
 * football-specific kit. The pieces are sized to fit the Mixamo Vanguard
 * humanoid (~1.83m tall) and parented to the head and chest bones at runtime.
 *
 * Per-team color tinting is applied via the shellColor / faceMaskColor /
 * padColor parameters so an offense and defense player can be visually
 * distinguished from across the field.
 *
 * Sprint 2 — Uniform overrides:
 *   PlayerModel passes a `uniformSpec` (resolved from
 *   Scene.uniform_overrides[team]) into createHelmet/createShoulderPads,
 *   so coaches can author custom team colours per-scene without forking
 *   the runtime. Each piece honours the spec independently:
 *     helmet.shellColor   ← spec.helmet  || spec.primary
 *     helmet.faceMaskColor ← spec.facemask
 *     pads.padColor       ← spec.pads    || spec.primary
 *     pads.accentColor    ← spec.accent  (drives the chest stripe)
 *   When no spec is supplied, behaviour falls back to the legacy
 *   single-color (shellColor / padColor) parameters.
 */

import * as THREE from 'three';

/** Convert "#rrggbb" / "0xRRGGBB" / number into a THREE-friendly hex int. */
export function parseHexColor(c, fallback = 0xffffff) {
  if (c == null) return fallback;
  if (typeof c === 'number') return c;
  const s = String(c).replace('#', '').replace(/^0x/i, '');
  const v = parseInt(s, 16);
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Resolve a UniformSpec dict (from `Scene.uniform_overrides[team]`) into the
 * runtime-friendly four-colour kit FootballGear+PlayerModel consume. Always
 * returns sensible fallbacks so the renderer never has to null-guard.
 *
 * Returns `{ jersey, helmet, pads, accent, facemask }` as hex ints.
 */
export function resolveUniformKit(spec, fallbacks = {}) {
  const safe = (key, dflt) => parseHexColor(
    (spec && spec[key]) ?? fallbacks[key] ?? dflt,
    parseHexColor(dflt, 0xffffff),
  );
  return {
    jersey:   safe('primary',  '#ffffff'),
    helmet:   parseHexColor((spec && (spec.helmet ?? spec.primary)) ?? fallbacks.helmet ?? '#ffffff'),
    pads:     parseHexColor((spec && (spec.pads ?? spec.primary)) ?? fallbacks.pads ?? '#ffffff'),
    accent:   safe('accent',   '#ffffff'),
    facemask: safe('facemask', '#202020'),
  };
}

function _mat(color, opts = {}) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: opts.roughness ?? 0.45,
    metalness: opts.metalness ?? 0.05,
  });
}

/**
 * Build a football helmet group: rounded shell + facemask cage + chinstrap.
 * Origin is placed at the bottom of the helmet (centered on the neck).
 */
export function createHelmet({ shellColor = 0xffffff, faceMaskColor = 0x222222 } = {}) {
  const group = new THREE.Group();
  group.name = 'helmet';

  // Shell sized to fully cover the Mixamo head (head bone bounding box
  // is ~22cm). Earlier 13cm shell sat behind the head and read as a
  // skull cap. 17cm radius + scale gives a true football-helmet
  // silhouette visible from QB / sideline distance.
  const shellGeo = new THREE.SphereGeometry(0.17, 24, 18, 0, Math.PI * 2, 0, Math.PI * 0.65);
  const shellMat = _mat(shellColor, { roughness: 0.25, metalness: 0.20 });
  const shell = new THREE.Mesh(shellGeo, shellMat);
  shell.position.y = 0.02;
  shell.scale.set(1.05, 1.10, 1.18);
  group.add(shell);

  // Bottom rim — closes the helmet visually so it doesn't look like a
  // half-sphere from behind.
  const rimGeo = new THREE.TorusGeometry(0.175, 0.018, 8, 24);
  const rim = new THREE.Mesh(rimGeo, shellMat);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = -0.02;
  rim.scale.set(1.0, 1.0, 1.18);
  group.add(rim);

  // Ear-hole cups
  const earGeo = new THREE.SphereGeometry(0.09, 16, 12);
  const earL = new THREE.Mesh(earGeo, shellMat);
  earL.position.set(-0.16, 0.0, 0);
  earL.scale.set(0.35, 0.85, 0.85);
  group.add(earL);
  const earR = earL.clone();
  earR.position.x = 0.16;
  group.add(earR);

  // Facemask cage — multi-bar grid out in FRONT of the helmet, which
  // is the most football-recognisable element on the rig. Built from
  // 2 horizontal bars + 2 vertical bars to match a typical cage style.
  const maskMat = _mat(faceMaskColor, { roughness: 0.35, metalness: 0.75 });

  const horizBarGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.22, 8);
  const upperBar = new THREE.Mesh(horizBarGeo, maskMat);
  upperBar.rotation.z = Math.PI / 2;
  upperBar.position.set(0, 0.06, 0.18);
  group.add(upperBar);

  const midBar = upperBar.clone();
  midBar.position.y = -0.01;
  group.add(midBar);

  const lowerBar = upperBar.clone();
  lowerBar.position.y = -0.08;
  group.add(lowerBar);

  const vertBarGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.18, 8);
  const vertL = new THREE.Mesh(vertBarGeo, maskMat);
  vertL.position.set(-0.07, -0.01, 0.18);
  group.add(vertL);
  const vertR = vertL.clone();
  vertR.position.x = 0.07;
  group.add(vertR);

  // Chin cup
  const chinGeo = new THREE.SphereGeometry(0.06, 12, 8, 0, Math.PI * 2, Math.PI * 0.4, Math.PI * 0.4);
  const chinMat = _mat(0xf0e8d8, { roughness: 0.7, metalness: 0.0 });
  const chin = new THREE.Mesh(chinGeo, chinMat);
  chin.position.set(0, -0.10, 0.10);
  chin.scale.set(1.0, 0.6, 0.8);
  group.add(chin);

  return group;
}

/**
 * Build shoulder pads: a bulky upper-body shell that sits on top of the
 * humanoid torso. Origin centered at chest height.
 */
export function createShoulderPads({
  padColor = 0xffffff,
  jerseyColor = null,
  accentColor = null,
} = {}) {
  const group = new THREE.Group();
  group.name = 'shoulder_pads';

  const padMat = _mat(padColor, { roughness: 0.55 });

  // Bigger yoke — shoulder pads are the second most football-defining
  // element after the helmet. Earlier 0.62m wide sat too narrow on
  // the Vanguard's broad shoulders so the silhouette read as "tactical
  // vest" not "football pads". 0.85m gives the boxy upper-body look.
  const yokeGeo = new THREE.BoxGeometry(0.85, 0.18, 0.42);
  const yoke = new THREE.Mesh(yokeGeo, padMat);
  yoke.position.y = 0.06;
  group.add(yoke);

  // Beveled top edge (rounded corners) — adds visual depth so the
  // pads don't look like a flat slab from above in top-down view.
  const bevelGeo = new THREE.BoxGeometry(0.83, 0.04, 0.40);
  const bevel = new THREE.Mesh(bevelGeo, padMat);
  bevel.position.y = 0.16;
  group.add(bevel);

  // Bigger shoulder caps — these are the bulky rounded humps that
  // sit on top of the deltoids and define the football silhouette.
  const capGeo = new THREE.SphereGeometry(0.18, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.55);
  const capL = new THREE.Mesh(capGeo, padMat);
  capL.position.set(-0.36, 0.07, 0);
  capL.scale.set(1.1, 1.0, 1.5);
  group.add(capL);
  const capR = capL.clone();
  capR.position.x = 0.36;
  group.add(capR);

  // Chest plate — slightly bigger and pushed further forward.
  const chestGeo = new THREE.BoxGeometry(0.46, 0.22, 0.20);
  const chest = new THREE.Mesh(chestGeo, padMat);
  chest.position.set(0, -0.10, 0.13);
  group.add(chest);

  // Accent stripe across the front of the yoke (team identity strip).
  if (accentColor !== null && accentColor !== undefined) {
    const stripeGeo = new THREE.BoxGeometry(0.85, 0.05, 0.43);
    const stripeMat = _mat(accentColor, { roughness: 0.45 });
    const stripe = new THREE.Mesh(stripeGeo, stripeMat);
    stripe.position.set(0, 0.0, 0);
    group.add(stripe);
  }

  if (jerseyColor !== null) {
    const numCanvas = document.createElement('canvas');
    numCanvas.width = 128; numCanvas.height = 64;
    const ctx = numCanvas.getContext('2d');
    ctx.fillStyle = `#${(typeof jerseyColor === 'number' ? jerseyColor : 0xffffff).toString(16).padStart(6, '0')}`;
    ctx.fillRect(0, 0, numCanvas.width, numCanvas.height);
  }

  return group;
}

/**
 * Build a procedural football: prolate spheroid with white stitching seam.
 */
export function createFootball() {
  const group = new THREE.Group();
  group.name = 'football';

  const ballGeo = new THREE.SphereGeometry(0.12, 20, 14);
  ballGeo.scale(1.0, 0.65, 0.65);
  const ballMat = new THREE.MeshStandardMaterial({ color: 0x6b3a1f, roughness: 0.85 });
  const ball = new THREE.Mesh(ballGeo, ballMat);
  group.add(ball);

  const seamGeo = new THREE.TorusGeometry(0.08, 0.004, 4, 24);
  const seamMat = new THREE.MeshStandardMaterial({ color: 0xfafafa, roughness: 0.5 });
  const seam = new THREE.Mesh(seamGeo, seamMat);
  seam.rotation.y = Math.PI / 2;
  group.add(seam);

  return group;
}

/**
 * Tint the team-color slot(s) of a humanoid to `jerseyColor`.
 *
 * Tier D contract: when the loaded GLB ships a uniform-mask material —
 * either via `userData.uniformMaskMaterial = <materialName>` on the
 * SkinnedMesh, or a material whose name ends in "_uniform" / starts
 * with "uniform_" — only that material slot is recolored. Skin,
 * helmet, cleats, sock stripes etc. keep their authored PBR maps so
 * the player still looks like a real athlete.
 *
 * Tier C contract (fallback): no mask metadata, no name match — the
 * whole skinned mesh is tinted. Same flat-color behaviour as before.
 *
 * @param humanoidRoot Three.Group returned by instantiateHumanoid().root
 * @param jerseyColor  team uniform color (hex int)
 * @param pantsColor   reserved for future use (currently ignored — Tier D
 *                     assets typically carry their own pants material; we
 *                     don't want to flatten it. Kept in the signature so
 *                     callers can wire it up once a Tier D asset declares
 *                     `userData.pantsMaskMaterial`.)
 */
export function tintHumanoidUniform(humanoidRoot, jerseyColor, /* pantsColor */ _pants) {
  const _isMaskName = (name) => {
    if (!name) return false;
    const n = String(name).toLowerCase();
    return n === 'uniform' || n.endsWith('_uniform') || n.startsWith('uniform_') || n.includes('jersey');
  };

  humanoidRoot.traverse((obj) => {
    if (!obj.isSkinnedMesh) return;

    const declaredMask = obj.userData && obj.userData.uniformMaskMaterial;
    const mats = Array.isArray(obj.material) ? obj.material : [obj.material];

    // Build a per-material tint decision so we can preserve the
    // original material on slots we shouldn't touch.
    const matched = mats.map((m) => {
      if (declaredMask) return m && m.name === declaredMask;
      if (m && _isMaskName(m.name)) return true;
      return false;
    });

    const anyMatch = matched.some(Boolean);

    if (!anyMatch) {
      // Tier C fallback: no uniform metadata, tint the whole mesh.
      const flat = new THREE.MeshStandardMaterial({
        color: jerseyColor,
        roughness: 0.6,
        metalness: 0.0,
        skinning: true,
      });
      flat.skinning = true;
      obj.material = Array.isArray(obj.material) ? mats.map(() => flat) : flat;
      return;
    }

    // Tier D path: clone only the matched slot(s) and recolor. Cloning
    // the material instead of mutating it keeps other player instances
    // (which share the cached GLTF template before SkeletonUtils.clone)
    // independent — recoloring in place would cross-contaminate teams.
    const newMats = mats.map((m, i) => {
      if (!matched[i] || !m) return m;
      const cloned = m.clone();
      cloned.color = new THREE.Color(jerseyColor);
      cloned.skinning = true;
      cloned.needsUpdate = true;
      return cloned;
    });
    obj.material = Array.isArray(obj.material) ? newMats : newMats[0];
  });
}
